import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { toHHmm } from '../newCalendar.helpers';

function TimeSlotEvent({ event, onReminderClick }) {
  const reminder = event.extendedProps.raw ?? {};

  const handleClick = (clickEvent) => {
    clickEvent.stopPropagation();
    onReminderClick(reminder);
  };

  return (
    <button
      type="button"
      className={clsx('new-calendar-time-slot-event', event.classNames)}
      onClick={handleClick}
    >
      <span className="new-calendar-time-slot-event__time">
        {toHHmm(reminder.starting_time ?? reminder.time)}
      </span>
      <strong className="new-calendar-time-slot-event__title">{event.title}</strong>
      <span className="new-calendar-time-slot-event__menu" aria-hidden="true">•••</span>
    </button>
  );
}

function TimeSlotEvents({ events, maxVisibleEvents, onReminderClick }) {
  return (
    <div
      className="new-calendar-time-slot-events"
      data-event-count={events.length}
      data-visible-events={maxVisibleEvents}
    >
      {events.map((event) => (
        <TimeSlotEvent key={event.id} event={event} onReminderClick={onReminderClick} />
      ))}
    </div>
  );
}

function GridEventContent({ event, timeText, isMonthView }) {
  return (
    <div className={clsx('new-calendar-grid-event', { 'new-calendar-grid-event--month': isMonthView })}>
      {timeText && <span>{timeText}</span>}
      <strong>{event.title}</strong>
      {isMonthView && <span className="new-calendar-grid-event__menu" aria-hidden="true">•••</span>}
    </div>
  );
}

function ListEventDetails({ event, className }) {
  const reminder = event.extendedProps.raw ?? {};

  return (
    <div className={clsx('new-calendar-list-event-details', className)}>
      <strong className="new-calendar-list-event-details__title">{event.title}</strong>
      <span className="new-calendar-list-event-details__description">{reminder.description ?? ''}</span>
    </div>
  );
}

function DatedListEventContent({ event }) {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || 'en';
  const reminder = event.extendedProps.raw ?? {};
  const occurrenceDate = event.extendedProps.occurrenceDate ?? reminder.starting_date;
  const weekday = event.start
    ? new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(event.start)
    : '';

  return (
    <div className="new-calendar-dated-list-event">
      <time className="new-calendar-dated-list-event__date" dateTime={occurrenceDate}>
        <span>{weekday}</span>
        <strong>{event.start?.getDate()}</strong>
      </time>
      <ListEventDetails event={event} className="new-calendar-dated-list-event__content" />
    </div>
  );
}

export function CalendarEventContent({ eventInfo, maxVisibleTimeSlotEvents, onReminderClick }) {
  const { event, timeText, view } = eventInfo;
  const timeSlotEvents = event.extendedProps.timeSlotEvents;

  if (Array.isArray(timeSlotEvents)) {
    return (
      <TimeSlotEvents
        events={timeSlotEvents}
        maxVisibleEvents={maxVisibleTimeSlotEvents}
        onReminderClick={onReminderClick}
      />
    );
  }

  if (!view.type.startsWith('list')) {
    return (
      <GridEventContent
        event={event}
        timeText={timeText}
        isMonthView={view.type === 'dayGridMonth'}
      />
    );
  }

  if (view.type === 'listMonth' || view.type === 'listWeek') {
    return <DatedListEventContent event={event} />;
  }

  return <ListEventDetails event={event} />;
}
